import kebabCase from 'lodash/kebabCase';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  getAllInteractiveElements,
  getOnFocusableElements,
  hasPressed,
} from '@/utility/accessibility';

import Tab, { TabSize } from '../Tab/Tab';
import css from './Tabs.module.css';

type ITabsElement = React.HTMLAttributes<HTMLUListElement>;

enum gradientSide {
  left = 'left',
  right = 'right',
  both = 'both',
}

export interface ITabItem {
  title: string;
  controlsId?: string;
  id?: string;
}

export interface IProps {
  active?: number;
  defaultActive?: number;
  items: ITabItem[];
  size?: TabSize;
  label?: string;
  className?: string;
  onChangeTab?: (index: number) => void;
}

const Tabs: React.FC<ITabsElement & IProps> = ({
  items,
  size = TabSize.normal,
  active,
  defaultActive = 0,
  label,
  onChangeTab,
  className = '',
  ...props
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const [internalActive, setInternalActive] = useState(defaultActive);
  const isControlled = typeof active !== 'undefined';
  const currentActive = isControlled ? active : internalActive;

  const handleTabClick = useCallback(
    (tabIndex: number) => () => {
      if (!isControlled) {
        setInternalActive(tabIndex);
      }
      onChangeTab?.(tabIndex);
    },
    [onChangeTab, isControlled],
  );

  const handleKeyListener = useCallback(
    (e: KeyboardEvent): void => {
      const tabs = ref.current;
      if (!tabs) return;

      const focusableElements = getAllInteractiveElements({
        container: tabs,
        focusableElements: ['button'],
        restrictions: [],
      });

      const { currentIndex, nextElement, previousElement } = getOnFocusableElements({
        container: tabs,
        focusableElements,
      });

      if (hasPressed(e).ENTER || hasPressed(e).SPACE) {
        isControlled ? onChangeTab?.(currentIndex) : setInternalActive(currentIndex);
      } else if (hasPressed(e).RIGHT) {
        nextElement.focus();
      } else if (hasPressed(e).LEFT) {
        previousElement.focus();
      }
    },
    [isControlled, onChangeTab],
  );

  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const tabs = ref.current;
    if (!tabs) return;

    setHasScroll(tabs.scrollWidth > tabs.clientWidth);

    tabs.addEventListener('keydown', handleKeyListener);
    return (): void => tabs.removeEventListener('keydown', handleKeyListener);
  }, [handleKeyListener]);

  const [scrollStatus, setScrollStatus] = useState('right');

  const handleScroll = throttle(() => {
    const scrollContainer = ref.current;
    if (!scrollContainer) return;

    const scrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    const scroll = scrollContainer.scrollLeft || 0;
    const startScroll = scroll < 10;
    const endScroll = scroll === scrollWidth;

    setScrollStatus(
      startScroll ? gradientSide.right : endScroll ? gradientSide.left : gradientSide.both,
    );
  }, 16);

  return (
    <div
      data-gradient={hasScroll ? scrollStatus || gradientSide.both : null}
      className={`relative ${css.tabs} ${className}`}>
      <ul
        ref={ref}
        onScroll={handleScroll}
        data-testid="tabs"
        aria-label={label}
        role="tablist"
        className={`relative flex py-2 overflow-x-auto whitespace-nowrap`}
        {...props}>
        {items.map((item, i) => {
          return (
            <li key={item.title} data-testid={`tab-${kebabCase(item.title)}`}>
              <Tab
                active={currentActive === i}
                size={size}
                tabIndex={currentActive === i ? 0 : -1}
                onClick={handleTabClick(i)}>
                {item.title}
              </Tab>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Tabs;
